import React, { useState, useEffect, useCallback } from 'react';
import { Container, Nav, Button, Spinner, Modal, Row, Col, Card, Badge } from 'react-bootstrap';
import { Routes, Route, Navigate, useLocation, Link, useNavigate } from 'react-router-dom';
import VehiclesView from './components/VehiclesView';
import ClientsView from './components/ClientsView';
import DriversView from './components/DriversView';
import TripsLogView from './components/TripsLogView';
import GlobalTripsView from './components/GlobalTripsView';
import ContractsView from './components/ContractsView';
import UnpaidReportsView from './components/UnpaidReportsView';
import TariffManager from './components/TariffManager';
import DispatchBoard from './components/DispatchBoard';
import SettlementsView from './components/SettlementsView';
import ClientBillingView from './components/ClientBillingView';
import TripAdminView from './components/TripAdminView';
import FleetMonitor from './components/FleetMonitor';
import SettlementGroupManager from './components/SettlementGroupManager';
import InsightsDashboard from './components/InsightsDashboard';
import HistoricalTripSearch from './components/HistoricalTripSearch';
import ProfileManager from './components/ProfileManager';
import AdminDocumentReview from './components/AdminDocumentReview';
import WhatsAppChatCenter from './components/WhatsAppChatCenter';
import BookingMeetingPointsManager from './components/BookingMeetingPointsManager';
import SmartBulkLoadView from './components/SmartBulkLoadView';
import TransfeeroManagerView from './components/TransfeeroManagerView';
import QuotesView from './quotes/QuotesView';
import useAuthStore, { isSuperAdminUser, SUPER_ADMIN_EMAILS } from '../store/useAuthStore';
import { openSuperAdminModal } from '../components/SuperAdminImpersonator';
import { getWhatsAppUnreadCount } from './api';
import { playLocutoraAlertSound } from './utils/soundAlerts';

const TransportDashboard = ({ isLoaded }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: currentUser, originalAdminUser } = useAuthStore();
  const [showFleetModal, setShowFleetModal] = useState(false);
  const [whatsappUnreadCount, setWhatsappUnreadCount] = useState(0);
  const [pendingQuotesCount, setPendingQuotesCount] = useState(0);
  const [attentionCount, setAttentionCount] = useState(0);
  const prevAttentionCountRef = React.useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await getWhatsAppUnreadCount();
      if (res && typeof res.unread_count === 'number') {
        setWhatsappUnreadCount(res.unread_count);
      }
      if (res && typeof res.pending_quotes_count === 'number') {
        setPendingQuotesCount(res.pending_quotes_count);
      }
      if (res && typeof res.attention_count === 'number') {
        if (prevAttentionCountRef.current !== null && res.attention_count > prevAttentionCountRef.current) {
          playLocutoraAlertSound();
        }
        prevAttentionCountRef.current = res.attention_count;
        setAttentionCount(res.attention_count);
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15000);
    const handleUpdate = () => fetchUnreadCount();
    window.addEventListener('whatsapp_unread_updated', handleUpdate);
    return () => {
      clearInterval(interval);
      window.removeEventListener('whatsapp_unread_updated', handleUpdate);
    };
  }, [fetchUnreadCount]);

  // Alerta visual en la pestaña del navegador cuando hay chats requiriendo atención
  useEffect(() => {
    if (attentionCount > 0) {
      const originalTitle = document.title;
      let toggle = false;
      const interval = setInterval(() => {
        document.title = toggle ? `🔴 (${attentionCount}) ¡Atención Requerida!` : 'Transportes Emilia';
        toggle = !toggle;
      }, 1400);
      return () => {
        clearInterval(interval);
        document.title = originalTitle;
      };
    }
  }, [attentionCount]);

  const userRole = (currentUser?.role || '').toLowerCase();
  const isAdmin = ['admin', 'gestor'].includes(userRole);
  const isLocutor = userRole === 'locutor';
  const isGestor = userRole === 'gestor';

  // Obtener permisos efectivos del usuario actual
  // null = acceso total (admin/gestor/operaciones), {} o dict = permisos granulares
  const userPerms = (() => {
    if (['admin', 'gestor', 'operaciones'].includes(userRole)) return null; // Acceso total
    if (currentUser?.permissions) return currentUser.permissions;
    // Locutor sin permisos configurados: defaults conservadores
    if (isLocutor) return {
      can_view_dispatch: true, can_view_control_room: true, can_view_fleet: true,
      can_view_drivers: true, can_view_fleet_mgmt: false, can_view_insights: false,
      can_view_billing: false, can_view_settlements: false, can_view_reports: false,
      can_view_clients: false, can_view_tariffs: false, can_view_admin_trips: false,
      can_view_booking_sim: false,
    };
    return {};
  })();

  // Helper: puede ver una sección?
  const can = (permKey) => {
    if (userPerms === null) return true; // Admin: acceso total
    return userPerms[permKey] === true;
  };

  // Helper para determinar la pestaña activa
  const getActiveKey = () => {
      const path = location.pathname;
      if (path.includes('/global-search')) return 'global-search';
      if (path.includes('/insights')) return 'insights';
      if (path.includes('/reports')) return 'reports';
      if (path.includes('/clients')) return 'clients';
      if (path.includes('/drivers')) return 'drivers';
      if (path.includes('/vehicles')) return 'vehicles';
      if (path.includes('/contracts')) return 'contracts';
      if (path.includes('/tariffs')) return 'tariffs';
      if (path.includes('/settlements')) return 'settlements';
      if (path.includes('/billing')) return 'billing';
      if (path.includes('/admin-trips')) return 'admin-trips';
      if (path.includes('/control-room')) return 'control-room';
      if (path.includes('/fleet')) return 'fleet';
      if (path.includes('/whatsapp')) return 'whatsapp';
      if (path.includes('/booking-pickups')) return 'booking-pickups';
      if (path.includes('/transfeero')) return 'transfeero';
      if (path.includes('/settlement-groups')) return 'settlement-groups';
      if (path.includes('/booking-simulator')) return 'booking-simulator';
      if (path.includes('/profiles')) return 'profiles';
      if (path.includes('/documents-review')) return 'documents-review';
      return 'dispatch';
  };

  if (!isLoaded) {
    return (
        <Container fluid className="p-4 d-flex justify-content-center align-items-center vh-100">
            <div className="text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Inicializando sistemas de mapas...</p>
            </div>
        </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0" style={{ paddingLeft: '44px' }}>🚖 Operaciones de Transporte</h2>
        <div className="d-flex gap-2 align-items-center">
          {currentUser && (
            <span className="me-2 text-muted fw-semibold">
              👤 {currentUser.name || currentUser.email}
            </span>
          )}
          {isSuperAdminUser(currentUser, originalAdminUser) && (
            <Button
              variant="dark"
              size="sm"
              className="fw-bold px-3 shadow-sm"
              style={{
                background: 'linear-gradient(90deg, #0f172a 0%, #311042 100%)',
                border: '1px solid #f59e0b',
                color: '#f8fafc',
              }}
              onClick={openSuperAdminModal}
              title="Entrar como cualquier proveedor, conductor o cliente sin clave"
            >
              👁️ Ver como...
            </Button>
          )}
          {/* Botón Finalizar Turno: visible para locutor y gestor */}
          {(isLocutor || isGestor) && (
            <Link to="/reloj">
              <Button variant="warning" className="fw-bold px-3" size="sm">
                ⏸ Finalizar Turno
              </Button>
            </Link>
          )}
          {!isLocutor && (
            <Link to="/">
              <Button variant="outline-secondary">← Volver</Button>
            </Link>
          )}
        </div>
      </div>

      {/* Banner de alerta para locutoras si hay pasajeros esperando atención */}
      {attentionCount > 0 && !location.pathname.includes('/whatsapp') && (
        <div 
          onClick={() => navigate('/transport/whatsapp')}
          className="mb-3 p-3 rounded-3 shadow-sm d-flex align-items-center justify-content-between text-white"
          style={{ 
            background: 'linear-gradient(90deg, #b91c1c 0%, #dc2626 50%, #991b1b 100%)', 
            cursor: 'pointer',
            border: '1px solid #f87171'
          }}
          role="button"
          title="Haga clic para ir directamente al chat y atender al pasajero"
        >
          <div className="d-flex align-items-center gap-3">
            <span style={{ fontSize: '1.75rem', lineHeight: 1 }}>🚨</span>
            <div>
              <div className="fw-bold fs-6">
                ¡Atención Requerida en WhatsApp! ({attentionCount} {attentionCount === 1 ? 'pasajero esperando' : 'pasajeros esperando'})
              </div>
              <div className="small text-white-50">
                Un pasajero solicitó atención de locutora humana o la IA transfirió la conversación para despacho.
              </div>
            </div>
          </div>
          <Button variant="light" size="sm" className="fw-bold text-danger px-3 shadow-sm">
            Ir al Chat WhatsApp →
          </Button>
        </div>
      )}

      <Nav variant="tabs" className="mb-3 overflow-auto flex-nowrap" activeKey={getActiveKey()}>
        {can('can_view_dispatch') && (
          <Nav.Item>
            <Nav.Link as={Link} to="/transport/dispatch" eventKey="dispatch">📅 Despacho</Nav.Link>
          </Nav.Item>
        )}
        {can('can_view_dispatch') && (
          <Nav.Item>
            <Nav.Link as={Link} to="/transport/global-search" eventKey="global-search" className="text-info fw-bold">🔎 Solicitudes</Nav.Link>
          </Nav.Item>
        )}
        {can('can_view_control_room') && (
          <Nav.Item>
            <Nav.Link as={Link} to="/transport/control-room" eventKey="control-room" className="text-danger fw-bold">🔥 Control</Nav.Link>
          </Nav.Item>
        )}
        {can('can_view_insights') && (
          <Nav.Item>
            <Nav.Link as={Link} to="/transport/insights" eventKey="insights" className="text-success fw-bold">📊 Insights</Nav.Link>
          </Nav.Item>
        )}
        {can('can_view_fleet') && (
          <Nav.Item>
            <Nav.Link as={Link} to="/transport/fleet" eventKey="fleet" className="text-primary fw-bold">📡 Monitor</Nav.Link>
          </Nav.Item>
        )}
        {can('can_view_dispatch') && (
          <Nav.Item>
            <Nav.Link 
              as={Link} 
              to="/transport/whatsapp" 
              eventKey="whatsapp" 
              className="text-purple fw-bold d-flex align-items-center gap-1" 
              style={{ color: '#8b5cf6' }}
            >
              💬 Chat WhatsApp
              {attentionCount > 0 && (
                <Badge 
                  bg="danger" 
                  pill 
                  className="ms-1 animate-pulse shadow-sm fw-bold border border-light" 
                  style={{ 
                    fontSize: '0.68rem', 
                    padding: '0.22em 0.55em',
                    backgroundColor: '#dc2626',
                    color: '#ffffff'
                  }}
                  title={`${attentionCount} pasajero(s) solicitan atención de locutora`}
                >
                  🚨 {attentionCount} Locutora
                </Badge>
              )}
              {pendingQuotesCount > 0 && (
                <Badge 
                  bg="warning" 
                  text="dark" 
                  pill 
                  className="ms-1 animate-pulse shadow-sm fw-bold" 
                  style={{ 
                    fontSize: '0.68rem', 
                    padding: '0.22em 0.55em',
                    backgroundColor: '#f59e0b',
                    color: '#000000'
                  }}
                  title={`${pendingQuotesCount} cotización(es) pendiente(s) de tarifa`}
                >
                  ⚡ {pendingQuotesCount} Cotizar
                </Badge>
              )}
              {whatsappUnreadCount > 0 && (
                <Badge 
                  bg="danger" 
                  pill 
                  className="ms-1 animate-pulse shadow-sm" 
                  style={{ 
                    fontSize: '0.68rem', 
                    padding: '0.22em 0.55em',
                    backgroundColor: '#ef4444' 
                  }}
                >
                  {whatsappUnreadCount}
                </Badge>
              )}
            </Nav.Link>
          </Nav.Item>
        )}
        {can('can_view_billing') && (
          <Nav.Item>
            <Nav.Link as={Link} to="/transport/billing" eventKey="billing">🧾 Facturación</Nav.Link>
          </Nav.Item>
        )}
        {can('can_view_admin_trips') && (
          <Nav.Item>
            <Nav.Link as={Link} to="/transport/admin-trips" eventKey="admin-trips" className="text-dark fw-bold">🛠 Admin</Nav.Link>
          </Nav.Item>
        )}
        {can('can_view_settlements') && (
          <Nav.Item>
            <Nav.Link as={Link} to="/transport/settlements" eventKey="settlements">💸 Liquidaciones</Nav.Link>
          </Nav.Item>
        )}
        {can('can_view_reports') && (
          <Nav.Item>
            <Nav.Link as={Link} to="/transport/reports" eventKey="reports">📋 Reportes</Nav.Link>
          </Nav.Item>
        )}
        {can('can_view_dispatch') && (
          <Nav.Item>
            <Nav.Link as={Link} to="/transport/smart-bulk-load" eventKey="smart-bulk-load" className="text-primary fw-bold">🪄 Carga Inteligente</Nav.Link>
          </Nav.Item>
        )}
        {can('can_view_clients') && (
          <Nav.Item>
            <Nav.Link as={Link} to="/transport/clients" eventKey="clients">🏢 Clientes</Nav.Link>
          </Nav.Item>
        )}
        {/* Flota: modal, visible si tiene permiso can_view_fleet_mgmt */}
        {can('can_view_fleet_mgmt') && (
          <Nav.Item>
            <Nav.Link onClick={() => setShowFleetModal(true)} style={{ cursor: 'pointer' }}>🚙 Flota</Nav.Link>
          </Nav.Item>
        )}
        {can('can_view_tariffs') && (
          <Nav.Item>
            <Nav.Link as={Link} to="/transport/tariffs" eventKey="tariffs">💰 Tarifas</Nav.Link>
          </Nav.Item>
        )}
        {(isAdmin || can('can_view_dispatch') || can('can_view_tariffs')) && (
          <Nav.Item>
            <Nav.Link as={Link} to="/transport/booking-pickups" eventKey="booking-pickups" className="fw-bold" style={{ color: '#003580' }}>
              📍 Pickups Booking
            </Nav.Link>
          </Nav.Item>
        )}
        {(isAdmin || can('can_view_dispatch') || can('can_view_tariffs')) && (
          <Nav.Item>
            <Nav.Link as={Link} to="/transport/transfeero" eventKey="transfeero" className="fw-bold" style={{ color: '#059669' }}>
              🚘 Transfeero
            </Nav.Link>
          </Nav.Item>
        )}
        {can('can_view_dispatch') && (
          <Nav.Item>
            <Nav.Link as={Link} to="/transport/historical" eventKey="historical" className="text-primary fw-bold">🔍 Histórico Anual</Nav.Link>
          </Nav.Item>
        )}
        {isAdmin && (
          <Nav.Item>
            <Nav.Link as={Link} to="/transport/profiles" eventKey="profiles" className="text-purple fw-bold" style={{ color: '#6366f1' }}>👥 Perfiles</Nav.Link>
          </Nav.Item>
        )}
        {(SUPER_ADMIN_EMAILS.includes(currentUser?.email?.trim()?.toLowerCase()) || can('can_view_quotes')) && (
          <Nav.Item>
            <Nav.Link as={Link} to="/transport/quotes" eventKey="quotes" className="text-primary fw-bold">📋 Cotizador</Nav.Link>
          </Nav.Item>
        )}
      </Nav>

      <Routes>
        <Route path="/" element={<Navigate to="dispatch" replace />} />
        {can('can_view_dispatch') && <Route path="dispatch" element={<TripsLogView isLoaded={isLoaded} />} />}
        {can('can_view_dispatch') && <Route path="smart-bulk-load" element={<SmartBulkLoadView />} />}
        {can('can_view_dispatch') && <Route path="global-search" element={<GlobalTripsView isLoaded={isLoaded} />} />}
        {can('can_view_dispatch') && <Route path="whatsapp" element={<WhatsAppChatCenter />} />}
        {can('can_view_control_room') && <Route path="control-room" element={<DispatchBoard isLoaded={isLoaded} />} />}
        {can('can_view_insights') && <Route path="insights" element={<InsightsDashboard />} />}
        {can('can_view_fleet') && <Route path="fleet" element={<FleetMonitor isLoaded={isLoaded} />} />}
        {can('can_view_billing') && <Route path="billing" element={<ClientBillingView isLoaded={isLoaded} />} />}
        {can('can_view_admin_trips') && <Route path="admin-trips" element={<TripAdminView isLoaded={isLoaded} />} />}
        {can('can_view_settlements') && <Route path="settlements" element={<SettlementsView />} />}
        {can('can_view_reports') && <Route path="reports" element={<UnpaidReportsView />} />}
        {can('can_view_clients') && <Route path="clients" element={<ClientsView />} />}
        {can('can_view_fleet_mgmt') && <Route path="drivers" element={<DriversView />} />}
        {can('can_view_fleet_mgmt') && <Route path="vehicles" element={<VehiclesView />} />}
        {can('can_view_fleet_mgmt') && <Route path="contracts" element={<ContractsView />} />}
        {can('can_view_tariffs') && <Route path="tariffs" element={<TariffManager isLoaded={isLoaded} />} />}
        {(isAdmin || can('can_view_dispatch') || can('can_view_tariffs')) && (
          <Route path="booking-pickups" element={<BookingMeetingPointsManager isAdmin={true} />} />
        )}
        {(isAdmin || can('can_view_dispatch') || can('can_view_tariffs')) && (
          <Route path="transfeero" element={<TransfeeroManagerView />} />
        )}
        {can('can_view_settlements') && <Route path="settlement-groups" element={<SettlementGroupManager />} />}
        {can('can_view_dispatch') && <Route path="historical" element={<HistoricalTripSearch isLoaded={isLoaded} />} />}
        {isAdmin && <Route path="profiles" element={<ProfileManager />} />}
        {isAdmin && <Route path="documents-review" element={<AdminDocumentReview />} />}
        {(SUPER_ADMIN_EMAILS.includes(currentUser?.email?.trim()?.toLowerCase()) || can('can_view_quotes')) && (
          <Route path="quotes" element={<QuotesView />} />
        )}
        <Route path="*" element={<Navigate to="dispatch" replace />} />
      </Routes>
      {/* Modal de Flota */}
      <Modal show={showFleetModal} onHide={() => setShowFleetModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-primary">🛸 Administración de Flota</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <Row className="g-4">
            <Col md={6}>
              <Card 
                className="h-100 border-0 bg-light" 
                style={{ 
                  cursor: 'pointer', 
                  transition: 'all 0.2s ease',
                  border: '1px solid #e0e0e0'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
                  e.currentTarget.style.borderColor = '#007bff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = '#e0e0e0';
                }}
                onClick={() => { navigate('/transport/drivers'); setShowFleetModal(false); }}
              >
                <Card.Body className="d-flex align-items-center p-4">
                  <div className="fs-1 me-3">👨‍✈️</div>
                  <div>
                    <h5 className="fw-bold mb-1">Conductores</h5>
                    <p className="text-muted small mb-0">Gestión de conductores, documentos y asignaciones.</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card 
                className="h-100 border-0 bg-light" 
                style={{ 
                  cursor: 'pointer', 
                  transition: 'all 0.2s ease',
                  border: '1px solid #e0e0e0'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
                  e.currentTarget.style.borderColor = '#007bff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = '#e0e0e0';
                }}
                onClick={() => { navigate('/transport/vehicles'); setShowFleetModal(false); }}
              >
                <Card.Body className="d-flex align-items-center p-4">
                  <div className="fs-1 me-3">🚙</div>
                  <div>
                    <h5 className="fw-bold mb-1">Móviles</h5>
                    <p className="text-muted small mb-0">Gestión de vehículos, patentes y estados.</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card 
                className="h-100 border-0 bg-light" 
                style={{ 
                  cursor: 'pointer', 
                  transition: 'all 0.2s ease',
                  border: '1px solid #e0e0e0'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
                  e.currentTarget.style.borderColor = '#007bff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = '#e0e0e0';
                }}
                onClick={() => { navigate('/transport/contracts'); setShowFleetModal(false); }}
              >
                <Card.Body className="d-flex align-items-center p-4">
                  <div className="fs-1 me-3">📜</div>
                  <div>
                    <h5 className="fw-bold mb-1">Contratos</h5>
                    <p className="text-muted small mb-0">Contratos de conductores y condiciones comerciales.</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card 
                className="h-100 border-0 bg-light" 
                style={{ 
                  cursor: 'pointer', 
                  transition: 'all 0.2s ease',
                  border: '1px solid #e0e0e0'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
                  e.currentTarget.style.borderColor = '#007bff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = '#e0e0e0';
                }}
                onClick={() => { navigate('/transport/settlement-groups'); setShowFleetModal(false); }}
              >
                <Card.Body className="d-flex align-items-center p-4">
                  <div className="fs-1 me-3">📋</div>
                  <div>
                    <h5 className="fw-bold mb-1">Grupos de Pago</h5>
                    <p className="text-muted small mb-0">Configuración de grupos para liquidaciones masivas.</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default TransportDashboard;